import React, { useRef, useState, useEffect } from 'react';
import LoginForm from './components/LoginForm';
import { APP_CONFIG } from '../../constants';
import {
  Mail,
  BarChart3,
  Zap,
  Users,
  TrendingUp,
  CheckCircle2,
  Shield,
  LayoutDashboard,
} from 'lucide-react';

const TRAIL_LERP = 0.12;
const TRAIL_RADIUS = 200;
const GREEN_HSL = 'var(--success)'; // green for grid trail

const FEATURES = [
  {
    icon: Zap,
    title: 'Automation',
    description: 'Workflows that run while you sleep',
  },
  {
    icon: Users,
    title: 'Smart segmentation',
    description: 'Target the right audience automatically',
  },
  {
    icon: TrendingUp,
    title: 'Advanced analytics',
    description: 'Track performance with detailed insights',
  },
  {
    icon: CheckCircle2,
    title: 'Easy setup',
    description: 'Get started in minutes',
  },
] as const;

const AuthPage: React.FC = () => {
  const panelRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);
  const rafRef = useRef<number>(0);

  const [trail, setTrail] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const tick = () => {
      const mouse = mouseRef.current;
      setTrail((prev) => {
        if (!mouse) return prev;
        const x = prev ? prev.x + (mouse.x - prev.x) * TRAIL_LERP : mouse.x;
        const y = prev ? prev.y + (mouse.y - prev.y) * TRAIL_LERP : mouse.y;
        return { x, y };
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    mouseRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    if (!trail) setTrail(mouseRef.current);
  };

  const handleMouseLeave = () => {
    mouseRef.current = null;
    setTrail(null);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel - App introduction (theme: sidebar + success accent) */}
      <div
        ref={panelRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="hidden lg:flex lg:w-[65%] flex-col bg-[hsl(var(--sidebar-background))] text-[hsl(var(--sidebar-foreground))] relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--sidebar-foreground)/0.03) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--sidebar-foreground)/0.03) 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
        }}
      >
        {/* Trailing green grid overlay – follows cursor with CSS mask; grid + glow are CSS-only visuals */}
        {trail && (
          <>
            <div
              className="pointer-events-none absolute inset-0 z-0"
              aria-hidden
              style={{
                backgroundImage: `linear-gradient(hsl(${GREEN_HSL}/0.4) 1px, transparent 1px),
                  linear-gradient(90deg, hsl(${GREEN_HSL}/0.4) 1px, transparent 1px)`,
                backgroundSize: '32px 32px',
                maskImage: `radial-gradient(circle at ${trail.x}px ${trail.y}px, black 0%, transparent ${TRAIL_RADIUS}px)`,
                WebkitMaskImage: `radial-gradient(circle at ${trail.x}px ${trail.y}px, black 0%, transparent ${TRAIL_RADIUS}px)`,
                maskSize: '100% 100%',
                maskRepeat: 'no-repeat',
                maskPosition: '0 0',
              }}
            />
            <div
              className="pointer-events-none absolute z-0 w-full h-full"
              aria-hidden
              style={{
                background: `radial-gradient(circle at ${trail.x}px ${trail.y}px, hsl(${GREEN_HSL}/0.12) 0%, transparent ${Math.round(TRAIL_RADIUS * 0.8)}px)`,
              }}
            />
          </>
        )}
        <div className="relative z-10 flex flex-col flex-1 p-10 xl:p-14 min-h-0">
          <div className="flex items-center gap-3 shrink-0 mt-8">
            <div className="w-11 h-11 bg-[hsl(var(--sidebar-foreground)/0.1)] rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Mail className="w-6 h-6 text-[hsl(var(--sidebar-foreground))]" />
            </div>
            <span className="text-xl font-semibold tracking-tight">
              {APP_CONFIG.NAME}
            </span>
          </div>

          <div className="flex-1 flex flex-col min-h-0 mt-16">
            <div className="grid xl:grid-cols-[1fr,1fr] gap-10 xl:gap-14 items-start">
              {/* Left: Hero + card */}
              <div className="space-y-8">
                <div className="bg-[hsl(var(--sidebar-foreground)/0.05)] backdrop-blur-sm rounded-2xl border border-[hsl(var(--sidebar-border))] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-[hsl(var(--sidebar-foreground)/0.9)]">
                      Campaign reach
                    </span>
                    <span className="text-xs text-[hsl(var(--sidebar-muted))]">
                      This month
                    </span>
                  </div>
                  <div className="flex items-end gap-4 h-24 mb-4">
                    <div
                      className="flex-1 rounded-t-md bg-[hsl(var(--sidebar-foreground)/0.2)]"
                      style={{ height: '60%' }}
                    />
                    <div
                      className="flex-1 rounded-t-md bg-[hsl(var(--sidebar-foreground)/0.3)]"
                      style={{ height: '85%' }}
                    />
                    <div
                      className="flex-1 rounded-t-md bg-[hsl(var(--success))]"
                      style={{ height: '100%' }}
                    />
                    <div
                      className="flex-1 rounded-t-md bg-[hsl(var(--sidebar-foreground)/0.25)]"
                      style={{ height: '70%' }}
                    />
                  </div>
                  <div className="flex items-center gap-2 text-[hsl(var(--sidebar-foreground)/0.8)]">
                    <BarChart3 className="w-4 h-4" />
                    <span className="text-sm">Deliverability & engagement</span>
                  </div>
                </div>

                <div>
                  <h2 className="text-3xl xl:text-4xl font-bold leading-tight text-[hsl(var(--sidebar-foreground))] mb-4">
                    Visualize, protect, and organize your email marketing.
                  </h2>
                  <p className="text-[hsl(var(--sidebar-foreground)/0.8)] text-lg leading-relaxed max-w-md">
                    Access your SegMailer dashboard to manage campaigns,
                    segments, and analytics in one place.
                  </p>
                </div>
              </div>

              {/* Right: Feature grid */}
              <div className="grid grid-cols-2 gap-4 xl:gap-5">
                {FEATURES.map(({ icon: Icon, title, description }) => (
                  <div
                    key={title}
                    className="bg-[hsl(var(--sidebar-foreground)/0.05)] backdrop-blur-sm rounded-xl border border-[hsl(var(--sidebar-border))] p-4 xl:p-5"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--success)/0.2)] flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-[hsl(var(--success))]" />
                    </div>
                    <h3 className="font-semibold text-[hsl(var(--sidebar-foreground))] text-sm xl:text-base mb-1">
                      {title}
                    </h3>
                    <p className="text-xs xl:text-sm text-[hsl(var(--sidebar-foreground)/0.75)] leading-snug">
                      {description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom: Trust strip */}
            <div className="mt-10 xl:mt-14 pt-8 xl:pt-10 border-t border-[hsl(var(--sidebar-border))] flex flex-wrap items-center gap-6 xl:gap-10 text-[hsl(var(--sidebar-foreground)/0.7)]">
              <div className="flex items-center gap-2">
                <LayoutDashboard className="w-5 h-5 text-[hsl(var(--success))]" />
                <span className="text-sm font-medium">
                  Single dashboard for everything
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[hsl(var(--success))]" />
                <span className="text-sm font-medium">Secure & reliable</span>
              </div>
              <span className="text-sm text-[hsl(var(--sidebar-muted))]">
                Trusted by teams worldwide
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - Form (theme: background, foreground, primary) */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12 bg-[hsl(40,20%,96%)]">
        <div className="w-full max-w-md">
          <LoginForm />
          <div className="mt-8 text-center">
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              By signing in, you agree to our{' '}
              <a
                href="/terms"
                className="text-[hsl(var(--primary))] underline hover:no-underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                className="text-[hsl(var(--primary))] underline hover:no-underline"
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
